import type { ExportNotificationEntry } from '@ikegps/core-api';
import type { CollectionModel } from '@ikegps/core-shared';
import { addStyles } from '../../components/theme';
import { T, Translate } from '../../i18n';
import type { MithrilVNode } from '../../mithril';
import { m } from '../../mithril';
import type { ExportNotificationCtrl } from './export.notification.ctrl';
import { ExportNotificationDetail } from './export.notification.detail/export.notification.detail';
import { ExportNotificationDetailCtrl } from './export.notification.detail/export.notification.detail.ctrl';
import { CSS, Style } from './export.notification.style';

addStyles(Style);

function viewNotificationDetail(
	notificationEntries: ExportNotificationEntry[],
	collection: CollectionModel
): MithrilVNode[] {
	return notificationEntries.map((notificationEntry) => {
		const detailCtrl = new ExportNotificationDetailCtrl(notificationEntry, collection);
		return ExportNotificationDetail.view(detailCtrl);
	});
}

function viewContent(ctrl: ExportNotificationCtrl): MithrilVNode {
	const children = ctrl.notifications.map((notification) => {
		const revision = notification.revision;
		const collection = ctrl.getCollection(revision);
		const entries = ctrl.errors ? notification.errors : notification.warnings;

		return m('div', { className: CSS.section }, [
			m('span', ctrl.getTitleFieldName(revision) + ': '),
			m('span', { className: CSS.title }, collection.title),
			m('ul', { className: CSS.content }, viewNotificationDetail(entries, collection)),
		]);
	});

	return m('div', { className: CSS.container }, children);
}

function viewTitle(ctrl: ExportNotificationCtrl): string {
	return ctrl.errors ? Translate(T.NOTIFICATION_ERROR_TITLE) : Translate(T.NOTIFICATION_WARNING_TITLE);
}

/**
 * Render a structured error message from the export validation.
 * Parses `Form "..."` sections and `  - "..."` field lines into bold headings with indented lists.
 * Falls back to plain divs for unstructured error strings.
 */
export function viewExportError(errorMessage: string): MithrilVNode {
	const lines = errorMessage.split('\n');

	const header: string[] = [];
	const sections: { formName: string; fields: string[] }[] = [];
	for (const line of lines) {
		if (line.startsWith('Form "')) {
			sections.push({ formName: line.replace(/:$/, ''), fields: [] });
		} else if (line.startsWith('  -') && sections.length > 0) {
			sections[sections.length - 1]!.fields.push(line.replace(/^\s+-\s*/, ''));
		} else if (line.trim()) {
			header.push(line);
		}
	}

	return m('div', { className: CSS.container }, [
		...header.map((h) => m('div', { className: CSS.section }, h)),
		...sections.map((section) =>
			m('div', { className: CSS.section }, [
				m('span', { className: CSS.title }, section.formName),
				m(
					'ul',
					{ className: CSS.content },
					section.fields.map((field) => m('li', field))
				),
			])
		),
	]);
}

export const ExportNotificationContent = { view: viewContent };
export const ExportNotificationTitle = { view: viewTitle };
